import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom";
import { ApiNames } from '../../components/Common/ApiManagement/apiServices'
import "./style.css"
import axiosInstance from "components/Common/ApiManagement/axiosInstance";
import { showToaster } from "components/Common/common";
import { setBreadcrumbItems } from "../../store/actions"
import { connect } from "react-redux"
import { useSelector } from "react-redux";
import {
  Building2,
  Search,
  Filter,
  MoreVertical,
  CheckCircle,
  XCircle,
  TrendingUp,
  Users,
  Shield
} from 'lucide-react';

const OrganaizationList = (props) => {
  document.title = "Organization Management";
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([])
  const [filteredOrgs, setFilteredOrgs] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all') // 'all', 'active', 'disabled'
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    disabled: 0
  })

  useEffect(() => {
    props.setBreadcrumbItems("Organization Management", [])
  }, [])

  const user = useSelector((state) => state?.users.users)

  const getScanList = async () => {
    try {
      const response = await axiosInstance.get(ApiNames.EDIT_ORG_LIST);
      const data = response.data.data;

      setOrganizations(data);
      setFilteredOrgs(data);

      // Calculate stats
      const activeCount = data.filter(org => org.status === 1).length;
      setStats({
        total: data.length,
        active: activeCount,
        disabled: data.length - activeCount
      });

    } catch (e) {
      console.error(e);
      showToaster(false, 'Failed to fetch organizations');
    }
  };

  useEffect(() => {
    if (user) getScanList();
  }, [user]);

  // Filter and search
  useEffect(() => {
    let filtered = [...organizations];

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(org =>
        filterStatus === 'active' ? org.status === 1 : org.status === 0
      );
    }

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(org =>
        org.branch?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.Office?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.SecurityContact?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredOrgs(filtered);
  }, [searchTerm, filterStatus, organizations]);

  const handleStatusToggle = async (item) => {
    try {
      const postForm = {
        _id: item._id,
        status: item.status === 1 ? 0 : 1,
        rememberMe: false
      }
      const response = await axiosInstance.post(ApiNames.EDIT_ORG_STATUS, postForm)

      if (response.data.status === 1) {
        await getScanList()
        await showToaster(true, `Organization ${item.status === 1 ? 'disabled' : 'activated'} successfully`)
      }
    } catch (error) {
      showToaster(false, 'Failed to update organization status');
    }
  }

  return (
    <div className="organization-management-page">
      {/* Header */}
      <div className="org-page-header">
        <div className="org-header-content">
          <div className="org-header-title-row">
            <Building2 className="org-header-icon" />
            <h1>Organization Management</h1>
          </div>
          <p className="org-header-subtitle">
            Manage and monitor all organizations in your compliance platform
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="org-stats-grid">
        <div className="org-stat-card stat-blue">
          <div className="stat-icon-wrapper stat-icon-blue">
            <Building2 className="stat-icon" />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Organizations</div>
            <div className="stat-value">{stats.total}</div>
          </div>
        </div>

        <div className="org-stat-card stat-green">
          <div className="stat-icon-wrapper stat-icon-green">
            <CheckCircle className="stat-icon" />
          </div>
          <div className="stat-content">
            <div className="stat-label">Active</div>
            <div className="stat-value">{stats.active}</div>
          </div>
        </div>

        <div className="org-stat-card stat-red">
          <div className="stat-icon-wrapper stat-icon-red">
            <XCircle className="stat-icon" />
          </div>
          <div className="stat-content">
            <div className="stat-label">Disabled</div>
            <div className="stat-value">{stats.disabled}</div>
          </div>
        </div>

        <div className="org-stat-card stat-purple">
          <div className="stat-icon-wrapper stat-icon-purple">
            <TrendingUp className="stat-icon" />
          </div>
          <div className="stat-content">
            <div className="stat-label">Active Rate</div>
            <div className="stat-value">
              {stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}%
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="org-controls-section">
        <div className="relative flex-1" style={{ minWidth: '300px' }}>
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search organizations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="org-filter-buttons">
          <button
            className={`filter-btn ${filterStatus === 'all' ? 'filter-active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            All ({stats.total})
          </button>
          <button
            className={`filter-btn ${filterStatus === 'active' ? 'filter-active' : ''}`}
            onClick={() => setFilterStatus('active')}
          >
            Active ({stats.active})
          </button>
          <button
            className={`filter-btn ${filterStatus === 'disabled' ? 'filter-active' : ''}`}
            onClick={() => setFilterStatus('disabled')}
          >
            Disabled ({stats.disabled})
          </button>
        </div>
      </div>

      {/* Organizations Table */}
      <div className="org-table-section">
        <div className="org-table-card">
          <div className="org-table-header">
            <h3>Organizations</h3>
            <span className="org-count">{filteredOrgs.length} {filteredOrgs.length === 1 ? 'organization' : 'organizations'}</span>
          </div>

          <div className="org-table-wrapper">
            <table className="org-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Organization Name</th>
                  <th>Office</th>
                  <th>Security Contact</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrgs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="org-table-empty">
                      <div className="empty-state">
                        <Building2 className="empty-icon" />
                        <p>No organizations found</p>
                        <span>Try adjusting your search or filters</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredOrgs.map((item, index) => (
                    <tr key={item._id || index}>
                      <td>
                        <span className="org-sno">{index + 1}</span>
                      </td>
                      <td>
                        <div className="org-name-cell">
                          <Building2 className="org-name-icon" />
                          <span className="org-name">{item.branch || "N/A"}</span>
                        </div>
                      </td>
                      <td>
                        <span className="org-office">{item.Office || "N/A"}</span>
                      </td>
                      <td>
                        <span className="org-contact">{item.SecurityContact || "N/A"}</span>
                      </td>
                      <td>
                        {item.status === 1 ? (
                          <span className="org-status-badge status-active">
                            <CheckCircle className="status-icon" />
                            Active
                          </span>
                        ) : (
                          <span className="org-status-badge status-disabled">
                            <XCircle className="status-icon" />
                            Disabled
                          </span>
                        )}
                      </td>
                      <td>
                        <label className="org-toggle-switch">
                          <input
                            type="checkbox"
                            checked={item.status === 1}
                            onChange={() => handleStatusToggle(item)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default connect(null, { setBreadcrumbItems })(OrganaizationList)
